import { defineStore } from 'pinia'
import tool from '@/utils/tool'
import { useGlobalStore } from '@/store/global'
import { isMobileUserDenied } from '@/router/mobileGuardUtils'

const PROJECT_KEY = 'MOBILE_CURRENT_PROJECT'

// 项目记忆按用户隔离：固定 key 会跨账号残留（A 退出 → B 登录后 restoreProject 恢复了 A 的项目）。
// key 挂上当前用户 id（userInfo 由 mobilePreparing 在路由守卫内先行加载，读取时已就绪）；
// 读不到用户 id（异常场景）时退回固定 key，保证功能可用
const projectKey = () => {
	const uid = useGlobalStore().userInfo?.id
	return uid ? `${PROJECT_KEY}_${uid}` : PROJECT_KEY
}

// 从「我的项目」myPage 行提取随会话记忆的项目元数据（监理文档提交/记录显隐判定消费，见 doc/doc-permission.js）
export const pickProjectMeta = (row) =>
	row ? { state: row.state, managerId: row.managerId, superviserId: row.superviserId } : undefined

// 项目页 tabs 静态定义：与菜单授权解耦（用户定案——tabs 无权限也可见，菜单只控制首页宫格入口）
// 任务 tab 按项目类型二选一，规则源码依据：jproject/index.vue L137-201（运维任务 v-if type==='2'，监理任务 v-if type==='1'）
const PROJECT_TAB_DEFS = [
	{ path: '/mobile/project/trouble', title: '问题管理' },
	{ path: '/mobile/project/task', title: '监理任务' },
	{ path: '/mobile/project/opTask', title: '运维任务' },
	{ path: '/mobile/project/doc', title: '监理文档' }
]

// 首页宫格静态注册表：显示与否复用 PC 端菜单授权（/sys/userCenter/loginMenu 返回树节点 code），
// 不再依赖 MOBILE_RESOURCE/loginMobileMenu（用户定案：授权不分端，PC 配一次两端生效）
// pcMenuCode 锚点：YvMuJXjsj=PC 日报管理›我的日报；drxzgD8vpG=PC 项目管理›我的项目
// code 存于 SYS_RESOURCE.CODE（drxzgD8vpG=我的项目 / YvMuJXjsj=我的日报），调整 PC 菜单 code 需同步此处
//（问题管理/监理任务/运维任务/监理文档在 PC 端均为"我的项目"页内功能，无独立菜单，统一挂 drxzgD8vpG）
const GRID_DEFS = [
	{ path: '/mobile/dailyReport', title: '填报日报', icon: 'edit-outlined', pcMenuCode: 'YvJMuJXjsj' },
	{ path: '/mobile/project', title: '我的项目', icon: 'folder-outlined', pcMenuCode: 'drxzgD8vpG' },
	{ path: '/mobile/project/trouble', title: '问题管理', icon: 'warning-outlined', pcMenuCode: 'drxzgD8vpG' },
	{ path: '/mobile/project/task', title: '监理任务', icon: 'schedule-outlined', pcMenuCode: 'drxzgD8vpG' },
	{ path: '/mobile/project/opTask', title: '运维任务', icon: 'tool-outlined', pcMenuCode: 'drxzgD8vpG' },
	{ path: '/mobile/project/doc', title: '监理文档', icon: 'file-text-outlined', pcMenuCode: 'drxzgD8vpG' }
]

// 移动端全局状态：菜单 + 当前项目上下文（localStorage 记忆）
export const mobileStore = defineStore({
	id: 'mobile',
	state: () => ({
		// PC 端授权菜单 code 集合（loginMenu 树展平收集，宫格入口判定依据）
		pcMenuCodes: [],
		// 当前项目 { projectId, projectName, type }（type: '1' 监理项目 / '2' 运维项目）
		currentProject: null,
		// 「我的项目」列表是否非空（首页/看板项目栏空态判定）
		hasProjects: false,
		// 会话是否已准备（userInfo/pcMenuCodes）
		prepared: false
	}),
	getters: {
		// 首页宫格入口：静态注册表按 PC 菜单授权过滤
		gridEntries(state) {
			return GRID_DEFS.filter((e) => state.pcMenuCodes.includes(e.pcMenuCode))
		},
		// 项目页 tabs（3 个）：问题管理 + 任务 tab（按项目类型二选一）+ 监理文档
		// 静态定义（PROJECT_TAB_DEFS），不依赖菜单授权——无角色用户也能使用项目页
		projectTabs(state) {
			const t = state.currentProject ? state.currentProject.type : null
			if (t === '1') return PROJECT_TAB_DEFS.filter((e) => e.path !== '/mobile/project/opTask')
			if (t === '2') return PROJECT_TAB_DEFS.filter((e) => e.path !== '/mobile/project/task')
			return PROJECT_TAB_DEFS
		},
		currentProjectId(state) {
			return state.currentProject ? state.currentProject.projectId : null
		}
	},
	actions: {
		async mobilePreparing() {
			await this.prepareUserInfo()
			// 页面级准入：已加载且 sys_user.TYPE 不在白名单 {1,2}（见 mobileGuardUtils.MOBILE_ALLOWED_USER_TYPES）
			// 不拉菜单、不置 prepared——后端登录接口已拦 device=APP，此处兜住 PC 登录态直达 /mobile/** 的场景，
			// 路由守卫会转 /mobile/notOpen；userInfo 未加载（令牌失效等）不在此拦截，走原有 401 路径
			if (isMobileUserDenied(useGlobalStore().userInfo)) {
				return
			}
			await this.prepareMenus()
			this.prepared = true
		},
		async prepareUserInfo() {
			// 复用 global store 的用户信息（userInfoFetching 自带幂等缓存）
			const gs = useGlobalStore()
			if (!gs.userInfo || !gs.userInfo.id) {
				await gs.userInfoFetching()
			}
		},
		async prepareMenus() {
			// 复用 PC 端 loginMenu（menusFetching 自带幂等缓存）：授权菜单树节点自带 code
			const gs = useGlobalStore()
			const tree = (await gs.menusFetching()) || []
			const codes = []
			const walk = (nodes) =>
				(nodes || []).forEach((node) => {
					if (node.code) codes.push(node.code)
					walk(node.children)
				})
			walk(tree)
			this.pcMenuCodes = codes
		},
		// projectId/projectName/type 均来自「我的项目」列表行（j_project 行数据）；
		// meta（state/managerId/superviserId）为监理文档提交权限判定快照（doc-permission.js 消费），
		// 由项目壳在 myPage 拉取后随行数据同步刷新（项目状态可能被 PC 端改变，避免会话内快照过期）
		setCurrentProject(projectId, projectName, type, meta) {
			this.currentProject = { projectId, projectName, type, ...(meta || {}) }
			tool.data.set(projectKey(), this.currentProject)
		},
		// 仅从 localStorage 恢复（本会话内存中未设置过当前项目时）；
		// key 已按用户隔离，不会恢复到其他账号的项目
		restoreProject() {
			if (this.currentProject) return this.currentProject
			const saved = tool.data.get(projectKey())
			if (saved && saved.projectId) {
				this.currentProject = saved
			}
			return this.currentProject
		},
		// 「我的项目」列表就绪后的统一收口（首页/项目页壳/看板项目栏共用）：
		// 1) 记忆项目仍在列表 → 回填元数据快照（state/managerId/superviserId 可能被 PC 端改变）；
		// 2) 无记忆或记忆项目已不在列表（换账号/项目完结）→ 取列表第一项；
		// 3) 列表为空 → 清空当前项目（页面据此走空态），并移除失效记忆
		ensureProjectContext(projects) {
			const list = projects || []
			this.hasProjects = list.length > 0
			const valid = list.find((p) => p.projectId === this.currentProjectId)
			if (valid) {
				this.setCurrentProject(valid.projectId, valid.projectName, valid.type, pickProjectMeta(valid))
				return
			}
			if (list.length) {
				const first = list[0]
				this.setCurrentProject(first.projectId, first.projectName, first.type, pickProjectMeta(first))
				return
			}
			this.currentProject = null
			tool.data.remove(projectKey())
		},
		clearMobile() {
			this.pcMenuCodes = []
			this.currentProject = null
			this.prepared = false
			this.hasProjects = false
			tool.data.remove(projectKey())
		}
	}
})
